import type { AuditionBasicsState } from './BrandAuditionBasicsStep';
import type { AuditionRole } from './BrandAuditionRolesStep';

const DRAFT_KEY = 'thspian_brand_audition_wizard_draft_v1';

export const AUDITION_WIZARD_INITIAL_BASICS: AuditionBasicsState = {
    title: '',
    audition_type: '',
    gig_type: 'cast',
    synopsis: '',
    plot: '',
    shoot_location: '',
    casting_expiration: '',
    job_start_date: '',
    min_age: '',
    max_age: '',
    required_skills: [],
    tags: [],
    type_of_work: '',
    union_affiliation: '',
    production_company: null,
    attachment: '',
    submission_report: 'daily digest',
    attachment_raw: null,
    publishStatus: 'publish',
    scheduled_published_date: '',
    imageCover: '',
    is_background_color: false,
    background_color: '',
};

type SerializableAuditionBasics = Omit<AuditionBasicsState, 'attachment_raw' | 'imageCover'> & {
    imageCover?: string;
};

export interface AuditionWizardDraftPayload {
    step: number;
    basics: SerializableAuditionBasics;
    roles: AuditionRole[];
    savedAt: string;
}

function serializeBasics(basics: AuditionBasicsState): SerializableAuditionBasics {
    const { attachment_raw: _file, imageCover, ...rest } = basics;
    let cover: string | undefined;
    if (imageCover === 'no-image') {
        cover = 'no-image';
    } else if (typeof imageCover === 'string') {
        cover = imageCover;
    }
    return { ...rest, ...(cover !== undefined ? { imageCover: cover } : {}) };
}

function deserializeBasics(serialized: SerializableAuditionBasics): AuditionBasicsState {
    return {
        ...AUDITION_WIZARD_INITIAL_BASICS,
        ...serialized,
        attachment_raw: null,
        imageCover: serialized.imageCover ?? '',
    };
}

export function loadAuditionWizardDraft(): { step: number; basics: AuditionBasicsState; roles: AuditionRole[] } | null {
    try {
        const raw = localStorage.getItem(DRAFT_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as AuditionWizardDraftPayload;
        if (!parsed || typeof parsed !== 'object') return null;
        return {
            step: typeof parsed.step === 'number' ? parsed.step : 0,
            basics: deserializeBasics(parsed.basics ?? ({} as SerializableAuditionBasics)),
            roles: Array.isArray(parsed.roles) ? parsed.roles : [],
        };
    } catch {
        return null;
    }
}

export function saveAuditionWizardDraft(step: number, basics: AuditionBasicsState, roles: AuditionRole[]): void {
    try {
        const payload: AuditionWizardDraftPayload = {
            step,
            basics: serializeBasics(basics),
            roles,
            savedAt: new Date().toISOString(),
        };
        localStorage.setItem(DRAFT_KEY, JSON.stringify(payload));
    } catch {
        /* ignore quota */
    }
}

export function clearAuditionWizardDraft(): void {
    try {
        localStorage.removeItem(DRAFT_KEY);
    } catch {
        /* ignore */
    }
}
